import {
  getQueryParts,
  IApiFilter,
  IApiListParams,
  useApi,
  useApiUrl,
} from "@src/composables/api";
import { useMoney } from "@src/composables/utils";
import { ICurrency } from "@src/models/Currency";
import { IProduct } from "@src/models/Product";
import _find from "lodash/find";
import _map from "lodash/map";
import _compact from "lodash/compact";
import { IProductCategory } from "@src/models/ProductCategory";
import { useI18n } from "./i18n";
import { Ref } from "vue";

export interface ICatalogueProductMapped {
  billingCycle: () => BillingCycleFormats;
  configUrl: URL;
  currentMonthlyFromPrice: string;
  currentPrice: string;
  currentSaving: string;
  description: string;
  excerpt: string;
  hasBillingCycle: boolean;
  hasDiscount: boolean;
  id: IProduct["id"];
  imgUrl: string;
  meta: Record<string, any> | null;
  name: string;
  regularMonthlyFromPrice: string;
  regularPrice: string;
}

export interface ICatalogueProductCategoryMapped {
  description: IProductCategory["description"];
  products_count: IProductCategory["products_count"];
  excerpt: IProductCategory["short_description"];
  id: IProductCategory["id"];
  meta: IProductCategory["meta"];
  name: IProductCategory["name"];
}

const { removeTrailingZeroes } = useMoney();

/**
 *
 * @name getProductConfigUrl
 * @desc Here we construct the product config URL
 */

function getProductConfigUrl(product: IProduct, options?: ICatalogueOptions) {
  const defaultOauthClient = _find(product?.brand?.oauth_clients, [
    "default",
    true,
  ]);
  const configUrl = options?.orderConfigUrl
    ? new URL(options.orderConfigUrl)
    : new URL("/order/product", `https://${defaultOauthClient?.origin}`);
  configUrl.searchParams.set("catid", product.category_id);
  configUrl.searchParams.set("pid", product.id);
  if (options?.currencyCode)
    configUrl.searchParams.set("currency", options.currencyCode);
  return configUrl;
}

function setProductRelations(url: URL) {
  // Define relations
  const relations = ["image", "prices", "brand.oauth_clients"];
  // Set `with` relations
  url.searchParams.set("with", relations.join(","));
}

function setProductCategoryRelations(url: URL) {
  // Define relations
  const relations = [
    "subcategories.image",
    "subcategories.subcategories.image",
    "subcategories.subcategories.subcategories.image",
    "subcategories.subcategories.subcategories.subcategories.image",
  ];
  // Set `with` relations
  url.searchParams.set("with", relations.join(","));
}

interface BillingCycleFormats {
  /** Adverbial variant e.g. `Monthly`, `Annually`, `One time`. */
  adverbial: string;
  /** Descriptive variant e.g. `month`, `3 years`. */
  descriptive: string;
  /** Monthly variant e.g., `month`, `6 months`. */
  monthly: string;
  /** Abbreviated suffix variant e.g. `mo` for 1 month, `2yr` for 2 years. */
  suffix: string;
  /** Numeric variant e.g. `1-month` or `2-year`. */
  numeric: string;
}

/**
 * Maps a billing cycle duration in months to various descriptive formats.
 *
 * @param months - The duration of the billing cycle in months.
 * @returns An object with multiple representations of the billing cycle
 */

function mapBillingCycle(months: number, options?: ICatalogueOptions) {
  const years = months / 12;
  const { $t, $tc } = useI18n({ locale: options?.locale, packs: ["core"] });

  switch (months) {
    case 0:
      return {
        adverbial: $t("core.once"), // Once
        descriptive: $t("core.one_time"), // One time
        monthly: $t("core.one_time"), // One time
        suffix: "", //
        numeric: $t("core.one_time"), // One time
      };
    case 1:
      return {
        adverbial: $t("core.monthly"), // Monthly
        descriptive: $tc("core.n_months", months), // month
        monthly: $tc("core.n_months", months), // month
        suffix: $tc("core.n_mo", months), // mo
        numeric: $t("core.n_month", { n: months.toString() }), // 1-month
      };
    case 3:
      return {
        adverbial: $t("core.quarterly"), // Quarterly
        descriptive: $tc("core.n_months", months), // 3 months
        monthly: $tc("core.n_months", months), // 3 months
        suffix: $tc("core.n_mo", months), // 3mo
        numeric: $t("core.n_month", { n: months.toString() }), // 3-month
      };
    case 6:
      return {
        adverbial: $t("core.semiannually"), // Semiannually
        descriptive: $tc("core.n_months", months), // 6 months
        monthly: $tc("core.n_months", months), // 6 months
        suffix: $tc("core.n_mo", months), // 6mo
        numeric: $t("core.n_month", { n: months.toString() }), // 6-month
      };
    case 12:
      return {
        adverbial: $t("core.annually"), // Annually
        descriptive: $tc("core.n_years", years), // year
        monthly: $tc("core.n_months", months), // 12 months
        suffix: $tc("core.n_yr", years), // yr
        numeric: $t("core.n_year", { n: years.toString() }), // 1-year
      };
    case 24:
      return {
        adverbial: $t("core.biennially"), // Biennially
        descriptive: $tc("core.n_years", years), // 2 years
        monthly: $tc("core.n_months", months), // 24 months
        suffix: $tc("core.n_yr", years), // 2yr
        numeric: $t("core.n_year", { n: years.toString() }), // 2-year
      };
    case 36:
      return {
        adverbial: $t("core.triennially"), // Triennially
        descriptive: $tc("core.n_years", years), // 3 years
        monthly: $tc("core.n_months", months), // 36 months
        suffix: $tc("core.n_yr", years), // 3yr
        numeric: $t("core.n_year", { n: years.toString() }), // 3-year
      };
    case 48:
    case 60:
    case 72:
    case 84:
    case 96:
    case 108:
    case 120:
      return {
        adverbial: $tc("core.n_years", years), // {n} years
        descriptive: $tc("core.n_years", years), // {n} years
        monthly: $tc("core.n_months", months), // {n} months
        suffix: $tc("core.n_yr", years), // {n}yr
        numeric: $t("core.n_year", { n: years.toString() }), // {n}-year
      };
    default:
      return {
        adverbial: $tc("core.n_months", months), // {n} months
        descriptive: $tc("core.n_months", months), // {n} months
        monthly: $tc("core.n_months", months), // {n} months
        suffix: $tc("core.n_mo", months), // {n}mo
        numeric: $t("core.n_month", { n: months.toString() }), // {n}-month
      };
  }
}

function transformPrice(
  price: string,
  amount: number,
  options?: ICatalogueOptions
) {
  // const { $t } = useI18n({ locale: options?.locale, packs: ["core"] });
  // return price;
  // return computed(() => {
  // Transform eg. 0.00 to "Free"
  // if (amount === 0 && options?.showZeroAsFree) return $t("core.free");
  // Transform eg. £10.00 to £10
  if (options?.trimTrailingZeroes) price = removeTrailingZeroes(price);
  // Return transformed price
  return price;
  // });
}

/**
 *
 * @name mapProduct
 * @desc Here we map the catalogue product
 */

function mapProduct(
  product: IProduct,
  options?: ICatalogueOptions
): ICatalogueProductMapped | undefined {
  // Get price object matching `display_price_billing_cycle_months`
  const priceObj = _find(product.prices, [
    "billing_cycle_months",
    product.display_price_billing_cycle_months,
  ]);

  if (!priceObj) return undefined;

  const hasBillingCycle = priceObj?.billing_cycle_months > 0;
  const hasDiscount = typeof priceObj?.price_discounted === "number";

  const regularAmount = priceObj?.price as number;
  const regularMonthlyFromAmount = priceObj?.monthly_price_from;
  const currentAmount = hasDiscount
    ? (priceObj?.price_discounted as number)
    : (priceObj?.price as number);
  const currentMonthlyFromAmount = hasDiscount
    ? priceObj?.monthly_price_from_discounted
    : priceObj?.monthly_price_from;

  const regularPrice = transformPrice(
    priceObj?.price_formatted,
    regularAmount,
    options
  );

  const regularMonthlyFromPrice = transformPrice(
    priceObj?.monthly_price_from_formatted,
    regularMonthlyFromAmount,
    options
  );

  const currentPrice = transformPrice(
    hasDiscount
      ? (priceObj?.price_discounted_formatted as string)
      : (priceObj?.price_formatted as string),
    currentAmount,
    options
  );

  const currentMonthlyFromPrice = transformPrice(
    hasDiscount
      ? (priceObj?.monthly_price_from_discounted_formatted as string)
      : (priceObj?.monthly_price_from_formatted as string),
    currentMonthlyFromAmount,
    options
  );

  const currentSavingAmount = hasDiscount
    ? ((regularAmount - currentAmount) / regularAmount) * 100
    : 0;
  const currentSaving = `${Math.round(currentSavingAmount)}%`;

  return {
    // Important: 'billingCycle' returns a function to keep reactivity of i18n
    billingCycle: () =>
      mapBillingCycle(priceObj?.billing_cycle_months, options),
    configUrl: getProductConfigUrl(product, options),
    currentMonthlyFromPrice,
    currentPrice,
    currentSaving,
    description: product.description_translated || product.description,
    excerpt: product.short_description_translated || product.short_description,
    hasBillingCycle,
    hasDiscount,
    id: product.id,
    imgUrl: product.image?.full_url + `?size=400x400`,
    meta: product.meta || null,
    name: product.name_translated || product.name,
    regularMonthlyFromPrice,
    regularPrice,
  };
}

/**
 *
 * @name mapCategory
 * @desc Here we map the catalogue product category
 */

function mapCategory(
  category: IProductCategory
): ICatalogueProductCategoryMapped {
  return {
    description: category.description_translated || category.description,
    products_count: category.products_count,
    excerpt:
      category.short_description_translated || category.short_description,
    id: category.id,
    meta: category.meta || null,
    name: category.name_translated || category.name,
  };
}

interface ICatalogueOptions {
  locale: Ref<string, string>;
  currencyCode?: ICurrency["code"];
  orderConfigUrl?: string;
  showLMFP?: boolean;
  showZeroAsFree?: boolean;
  trimTrailingZeroes?: boolean;
}

export function useCatalogue(options: ICatalogueOptions) {
  const { useApiInfiniteList, useApiList, useApiPaginatedList, useApiQuery } =
    useApi();

  const setCurrencyFilter =
    (currencyCode?: ICurrency["code"]): IApiFilter =>
    (url) => {
      if (currencyCode) url.searchParams.set("currency_code", currencyCode);
      return url;
    };

  const setProductCategoryFilter =
    (categoryId?: string): IApiFilter =>
    (url) => {
      if (categoryId)
        url.searchParams.set("filter[products_category_id]", categoryId);
      return url;
    };

  const setIdsFilter =
    (ids?: string[]): IApiFilter =>
    (url) => {
      if (ids?.length) url.searchParams.set("filter[id]", ids.join());
      return url;
    };

  const setCouponsFilter =
    (coupons?: string[]): IApiFilter =>
    (url) => {
      if (coupons?.length)
        url.searchParams.set("promotions", coupons.join(","));
      return url;
    };

  /**
   *
   * @name getProduct
   * @param id - Product ID.
   * @desc Here we fetch a catalogue product (by ID), and return a mapped
   * product object ready to be used within components.
   */

  const getProduct = (id: string, filters: IApiFilter[] = []) => {
    // Construct query URL
    const queryUrl = useApiUrl(`basket/products/${id}`, filters);
    // Construct query parts derived from filters
    const queryParts = getQueryParts(queryUrl);
    // Define relations
    const relations = ["image", "prices", "brand.oauth_clients"];
    // Set `with` relations
    queryUrl.searchParams.set("with", relations.join(","));

    return useApiQuery(
      {
        queryKey: _compact([`single::product::${id}`, ...queryParts]),
        queryUrl,
        queryMapper: (product: IProduct) => mapProduct(product, options),
      },
      {
        enabled: !!id,
        staleTime: 5000 * 60, // 5 minutes
      }
    );
  };

  /**
   * Fetches infinite product data using `useInfiniteQuery` from TanStack Query.
   *
   * @param listParams - Query params including limit and filter functions.
   * @returns {UseInfiniteQueryResult} Result from `useInfiniteQuery`.
   */

  function getInfiniteProducts({ filters, sort, limit }: IApiListParams) {
    // Construct query URL
    const queryUrl = useApiUrl(`basket/products`, filters);
    // Construct query parts derived from filters
    const queryParts = getQueryParts(queryUrl);
    // Set universal product relations
    setProductRelations(queryUrl);

    return useApiInfiniteList({
      listKey: ["infinite::products", ...queryParts],
      listParams: { sort, limit },
      listMethod: (params, signal) =>
        useApiList({ ...params, url: queryUrl }, signal, (product: IProduct) =>
          mapProduct(product, options)
        ),
    });
  }

  /**
   * Fetches paginated product data using `useQuery` from TanStack Query.
   *
   * @param listParams - Query params including limit and filter functions.
   * @returns {UseInfiniteQueryResult} Result from `useInfiniteQuery`.
   */

  function getPaginatedProducts({ filters, sort, limit }: IApiListParams) {
    // Construct query URL
    const queryUrl = useApiUrl(`basket/products`, filters);
    // Construct query parts derived from filters
    const queryParts = getQueryParts(queryUrl);
    // Set universal product relations
    setProductRelations(queryUrl);

    return useApiPaginatedList({
      listKey: ["paginated::products", ...queryParts],
      listParams: { sort, limit },
      listMethod: (params, signal) =>
        useApiList({ ...params, url: queryUrl }, signal, (product: IProduct) =>
          mapProduct(product, options)
        ),
    });
  }

  /**
   * Fetches infinite product data using `useInfiniteQuery` from TanStack Query.
   *
   * @param listParams - Query params including limit and filter functions.
   * @returns {UseInfiniteQueryResult} Result from `useInfiniteQuery`.
   */

  function getInfiniteCategories({ filters, sort, limit }: IApiListParams) {
    // Construct query URL
    const queryUrl = useApiUrl(`basket/products_categories`, filters);
    // Construct query parts derived from filters
    const queryParts = getQueryParts(queryUrl);
    // Set universal product category relations
    setProductCategoryRelations(queryUrl);

    return useApiInfiniteList({
      listKey: ["infinite::product_categories", ...queryParts],
      listParams: { sort, limit },
      listMethod: (params, signal) =>
        useApiList(
          { ...params, url: queryUrl },
          signal,
          (category: IProductCategory) => mapCategory(category)
        ),
    });
  }

  return {
    // Getters
    getProduct,
    getInfiniteCategories,
    getInfiniteProducts,
    getPaginatedProducts,
    // Setters
    setCurrencyFilter,
    setCouponsFilter,
    setIdsFilter,
    setProductCategoryFilter,
  };
}
